// src/pages/HRStudentFormDashboard.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    FiUsers, FiFileText, FiSearch, FiFilter, FiCheckCircle, FiClock,
    FiAlertCircle, FiRefreshCw, FiEye, FiEdit2, FiSend, FiCalendar,
    FiChevronDown, FiX, FiSave, FiDollarSign, FiChevronLeft,
    FiChevronRight, FiUser, FiInfo, FiBell, FiPlus
} from 'react-icons/fi';
import { useStudentFormContext } from '../context/StudentFormContext';
import { getCurrentUser } from '../services/authService';
import { format, formatDistanceToNow } from 'date-fns';
import { useLocation } from 'react-router-dom';

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS (pure, outside component — stable across renders)
// ─────────────────────────────────────────────────────────────────────────────

const formatDate = (date) => {
    if (!date) return 'N/A';
    try { return format(new Date(date), 'dd MMM yyyy'); } catch { return 'N/A'; }
};

const formatDateDisplay = (date) => {
    if (!date) return '-';
    try { return format(new Date(date), 'dd MMM yyyy, hh:mm a'); } catch { return '-'; }
};

const formatCurrency = (amount) => {
    if (!amount || amount === '0.00' || amount === 0) return '₹0.00';
    return `₹${parseFloat(amount).toFixed(2)}`;
};

const getTodayDate = () => format(new Date(), 'yyyy-MM-dd');

const isRecentlyUpdated = (updatedAt) => {
    const diffHours = (new Date() - new Date(updatedAt)) / (1000 * 60 * 60);
    return diffHours <= 24;
};

// Check if form is new (submitted within last 5 minutes)
const isNewForm = (createdAt) => {
    const diffMinutes = (new Date() - new Date(createdAt)) / (1000 * 60);
    return diffMinutes <= 5;
};

const getStatusBadge = (form) => {
    if (form.courier_number)
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">Couriered</span>;
    if (form.print_done === 'yes')
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">Printed</span>;
    if (form.amount_verified)
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">Verified</span>;
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">Pending</span>;
};

const getCardBorderColor = (form) => {
    if (form.courier_number) return 'border-green-500';
    if (form.print_done === 'yes') return 'border-blue-500';
    if (form.amount_verified) return 'border-yellow-500';
    return 'border-red-500';
};

// ─────────────────────────────────────────────────────────────────────────────
// TOAST NOTIFICATION COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const ToastNotification = ({ notification, onClose }) => {
    if (!notification) return null;

    const { type, employeeName, studentName, formId, timestamp, message } = notification;
    const isNew = type === 'new';
    const icon = isNew ? <FiPlus className="text-white" /> : <FiEdit2 className="text-white" />;
    const bgColor = isNew ? 'bg-green-500' : 'bg-blue-500';
    const title = isNew ? 'New Student Form Submitted' : 'Student Form Updated';

    return (
        <div className="fixed top-20 right-4 z-50 max-w-md w-full animate-slide-in-right">
            <div className={`${bgColor} text-white rounded-2xl shadow-2xl overflow-hidden`}>
                <div className="p-4 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                        {icon}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm">{title}</p>
                        <p className="text-xs opacity-90 mt-0.5">
                            <span className="font-medium">Employee:</span> {employeeName}
                        </p>
                        <p className="text-xs opacity-90">
                            <span className="font-medium">Student:</span> {studentName}
                        </p>
                        <p className="text-xs opacity-75 mt-1">
                            {formatDistanceToNow(new Date(timestamp), { addSuffix: true })}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-white/20 rounded-lg transition flex-shrink-0"
                    >
                        <FiX size={16} />
                    </button>
                </div>
                {/* Progress bar that shrinks to auto-dismiss */}
                <div className="h-1 bg-white/30">
                    <div className="h-full bg-white animate-progress-shrink" />
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// DETAIL MODAL
// ─────────────────────────────────────────────────────────────────────────────
const DetailModal = ({ selectedForm, onClose, onEdit }) => {
    if (!selectedForm) return null;
    const form = selectedForm;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
            <div className="relative min-h-screen flex items-center justify-center p-4">
                <div className="relative bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="sticky top-0 bg-white z-10 p-6 border-b border-gray-200 flex items-center justify-between">
                        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                            <FiFileText className="text-indigo-600" />
                            Form Details
                        </h3>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition">
                            <FiX size={20} />
                        </button>
                    </div>

                    <div className="p-6 space-y-6">
                        {/* Student Info */}
                        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-4">
                            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2 mb-3">
                                <FiUser className="text-indigo-600" /> Student Information
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {[
                                    ['Student Name', form.student_name],
                                    ['Employee', `${form.employee_info?.name || '-'} (${form.employee_info?.employee_id || '-'})`],
                                    ['Center', form.center_name],
                                    ['Enrolment Number', form.enrolment_number],
                                    ['Roll No. — 1st Year', form.roll_number_first_year || '-'],
                                    ['Roll No. — 2nd Year', form.roll_number_second_year || '-'],
                                    ['NOC', form.noc === 'yes' ? 'Yes' : 'No'],
                                    ['NOC Display', form.noc_display || '-'],
                                ].map(([label, value]) => (
                                    <div key={label} className="bg-white rounded-lg p-3">
                                        <p className="text-xs text-gray-500 font-medium">{label}</p>
                                        <p className="text-sm font-semibold text-gray-800">{value}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Payment */}
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4">
                            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2 mb-3">
                                <FiDollarSign className="text-green-600" /> Payment Details
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="bg-white rounded-lg p-3">
                                    <p className="text-xs text-gray-500 font-medium">Amount</p>
                                    <p className="text-sm font-semibold text-gray-800">{formatCurrency(form.amount)}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3">
                                    <p className="text-xs text-gray-500 font-medium">Transaction ID</p>
                                    <p className="text-sm font-semibold text-gray-800">{form.transaction_id || '-'}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3">
                                    <p className="text-xs text-gray-500 font-medium">Amount Verified</p>
                                    <p className="text-sm font-semibold">
                                        {form.amount_verified
                                            ? <span className="text-green-600 flex items-center gap-1"><FiCheckCircle size={16} /> Yes</span>
                                            : <span className="text-yellow-600 flex items-center gap-1"><FiClock size={16} /> No</span>
                                        }
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Verification */}
                        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4">
                            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2 mb-3">
                                <FiCheckCircle className="text-purple-600" /> Verification Details
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="bg-white rounded-lg p-3">
                                    <p className="text-xs text-gray-500 font-medium">Print Status</p>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${form.print_done === 'yes' ? 'bg-blue-100 text-blue-700'
                                        : form.print_done === 'no' ? 'bg-gray-100 text-gray-600'
                                            : 'bg-yellow-100 text-yellow-700'
                                        }`}>
                                        {form.print_done || 'pending'}
                                    </span>
                                </div>
                                <div className="bg-white rounded-lg p-3">
                                    <p className="text-xs text-gray-500 font-medium">Print Date</p>
                                    <p className="text-sm font-semibold text-gray-800">{formatDate(form.printing_date)}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3">
                                    <p className="text-xs text-gray-500 font-medium">Courier Date</p>
                                    <p className="text-sm font-semibold text-gray-800">{formatDate(form.courier_date)}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3">
                                    <p className="text-xs text-gray-500 font-medium">Courier Number</p>
                                    <p className="text-sm font-semibold text-gray-800">{form.courier_number || '-'}</p>
                                </div>
                            </div>
                        </div>

                        {/* Other */}
                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4">
                            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2 mb-3">
                                <FiInfo className="text-gray-600" /> Other Information
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="bg-white rounded-lg p-3">
                                    <p className="text-xs text-gray-500 font-medium">Created</p>
                                    <p className="text-sm font-semibold text-gray-800">{formatDateDisplay(form.created_at)}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3">
                                    <p className="text-xs text-gray-500 font-medium">Updated</p>
                                    <p className="text-sm font-semibold text-gray-800">{formatDateDisplay(form.updated_at)}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3 md:col-span-2">
                                    <p className="text-xs text-gray-500 font-medium">Remarks</p>
                                    <p className="text-sm font-semibold text-gray-800">{form.remark || '-'}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="sticky bottom-0 bg-white p-4 border-t border-gray-200 flex justify-end gap-3">
                        <button
                            onClick={() => onEdit(form)}
                            className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition flex items-center gap-2"
                        >
                            <FiEdit2 size={16} /> Edit Form
                        </button>
                        <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition">
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// EDIT MODAL
// ─────────────────────────────────────────────────────────────────────────────
const EditModal = ({ selectedForm, editData, setEditData, dateErrors, setDateErrors, onClose, onSave }) => {
    if (!selectedForm) return null;
    const todayDate = getTodayDate();

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
            <div className="relative min-h-screen flex items-center justify-center p-4">
                <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="sticky top-0 bg-white z-10 p-6 border-b border-gray-200 flex items-center justify-between">
                        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                            <FiEdit2 className="text-green-600" /> HR Update
                        </h3>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition">
                            <FiX size={20} />
                        </button>
                    </div>

                    <div className="p-6 space-y-4">
                        <div className="bg-gray-50 rounded-xl p-3 text-sm grid grid-cols-2 gap-2">
                            <div><span className="font-medium text-gray-500">Student:</span> <span className="text-gray-800">{selectedForm.student_name}</span></div>
                            <div><span className="font-medium text-gray-500">Enrolment:</span> <span className="text-gray-800">{selectedForm.enrolment_number}</span></div>
                            <div><span className="font-medium text-gray-500">Amount:</span> <span className="text-gray-800 font-semibold">{formatCurrency(selectedForm.amount)}</span></div>
                            <div><span className="font-medium text-gray-500">Employee:</span> <span className="text-gray-800">{selectedForm.employee_info?.name || '-'}</span></div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Amount Verified</label>
                                <select
                                    value={editData.amount_verified ? 'true' : 'false'}
                                    onChange={(e) => setEditData(prev => ({ ...prev, amount_verified: e.target.value === 'true' }))}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 bg-white"
                                >
                                    <option value="false">No</option>
                                    <option value="true">Yes</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Print Status</label>
                                <select
                                    value={editData.print_done || 'pending'}
                                    onChange={(e) => setEditData(prev => ({ ...prev, print_done: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 bg-white"
                                >
                                    <option value="pending">Pending</option>
                                    <option value="no">No</option>
                                    <option value="yes">Yes</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Print Date</label>
                                <input
                                    type="date"
                                    value={editData.printing_date || ''}
                                    onChange={(e) => {
                                        setEditData(prev => ({ ...prev, printing_date: e.target.value }));
                                        setDateErrors(prev => ({ ...prev, printing_date: '' }));
                                    }}
                                    max={todayDate}
                                    className={`w-full px-3 py-2 border ${dateErrors.printing_date ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:outline-none focus:border-indigo-400`}
                                />
                                {dateErrors.printing_date && <p className="text-xs text-red-500 mt-1">{dateErrors.printing_date}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Courier Date</label>
                                <input
                                    type="date"
                                    value={editData.courier_date || ''}
                                    onChange={(e) => {
                                        setEditData(prev => ({ ...prev, courier_date: e.target.value }));
                                        setDateErrors(prev => ({ ...prev, courier_date: '' }));
                                    }}
                                    max={todayDate}
                                    className={`w-full px-3 py-2 border ${dateErrors.courier_date ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:outline-none focus:border-indigo-400`}
                                />
                                {dateErrors.courier_date && <p className="text-xs text-red-500 mt-1">{dateErrors.courier_date}</p>}
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Courier Number</label>
                                <input
                                    type="text"
                                    value={editData.courier_number || ''}
                                    onChange={(e) => setEditData(prev => ({ ...prev, courier_number: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400"
                                    placeholder="Enter courier tracking number"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Remark</label>
                                <textarea
                                    value={editData.remark || ''}
                                    onChange={(e) => setEditData(prev => ({ ...prev, remark: e.target.value }))}
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400"
                                    placeholder="Add any remarks..."
                                />
                            </div>
                        </div>
                    </div>

                    <div className="sticky bottom-0 bg-white p-4 border-t border-gray-200 flex justify-end gap-3">
                        <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition">
                            Cancel
                        </button>
                        <button
                            onClick={onSave}
                            className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition flex items-center gap-2"
                        >
                            <FiSave size={16} /> Update HR Fields
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// EMPLOYEE PAGINATION
// ─────────────────────────────────────────────────────────────────────────────
const EmployeePagination = ({ employeeKey, currentPage, totalPages, totalItems, onPageChange }) => {
    if (totalItems <= 3) return null;
    return (
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
            <div className="text-xs text-gray-500">
                Showing {Math.min((currentPage - 1) * 3 + 1, totalItems)} to {Math.min(currentPage * 3, totalItems)} of {totalItems}
            </div>
            <div className="flex items-center gap-1">
                <button
                    onClick={() => onPageChange(employeeKey, currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`p-1.5 rounded-lg transition ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                    <FiChevronLeft size={14} />
                </button>
                <span className="text-xs text-gray-600 px-2">Page {currentPage} of {totalPages}</span>
                <button
                    onClick={() => onPageChange(employeeKey, currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`p-1.5 rounded-lg transition ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                    <FiChevronRight size={14} />
                </button>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const HRStudentFormDashboard = () => {
    const { forms, loading, error, hrUpdateForm, refreshData, initialized } = useStudentFormContext();
    const currentUser = useMemo(() => getCurrentUser(), []);
    const isAdminOrHR = currentUser?.role === 'admin' || currentUser?.role === 'hr_manager';
    const isMounted = useRef(true);
    const location = useLocation();
    const [dataLoaded, setDataLoaded] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // UI state
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [expandedEmployees, setExpandedEmployees] = useState(new Set());
    const [employeePages, setEmployeePages] = useState({});

    // Modal state
    const [selectedForm, setSelectedForm] = useState(null);
    const [modalType, setModalType] = useState(null);
    const [editData, setEditData] = useState({});
    const [dateErrors, setDateErrors] = useState({});

    // Notification state
    const [notification, setNotification] = useState(null);
    const [newFormIds, setNewFormIds] = useState(new Set());
    const [updatedFormIds, setUpdatedFormIds] = useState(new Set());
    const [prevForms, setPrevForms] = useState([]);

    // URL param filter
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const status = params.get('status');
        if (status && ['pending', 'verified', 'printed', 'couriered'].includes(status)) {
            setFilterStatus(status);
        }
    }, [location.search]);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    // ── NEW: Detect new/updated forms and show notifications ──────────────
    useEffect(() => {
        if (!initialized || forms.length === 0) return;

        // Check for new forms (comparing with previous forms list)
        if (prevForms.length > 0) {
            // Find new forms
            const newForms = forms.filter(form =>
                !prevForms.some(prev => prev.id === form.id)
            );

            // Find updated forms (existing but changed)
            const updatedForms = forms.filter(form => {
                const prev = prevForms.find(p => p.id === form.id);
                return prev && (
                    prev.amount_verified !== form.amount_verified ||
                    prev.print_done !== form.print_done ||
                    prev.courier_number !== form.courier_number ||
                    prev.remark !== form.remark
                );
            });

            // Process new forms
            newForms.forEach(form => {
                // Add to new form IDs
                setNewFormIds(prev => new Set(prev).add(form.id));

                // Show toast notification
                setNotification({
                    type: 'new',
                    employeeName: form.employee_info?.name || 'Unknown Employee',
                    studentName: form.student_name || 'Unknown Student',
                    formId: form.id,
                    timestamp: form.created_at,
                    message: 'New student form submitted'
                });

                // Auto-expand the employee card
                const employeeKey = `${form.employee_info?.employee_id || 'unknown'}-${form.employee_info?.name || 'Unknown Employee'}`;
                setExpandedEmployees(prev => new Set(prev).add(employeeKey));

                // Auto-dismiss notification after 5 seconds
                setTimeout(() => {
                    setNotification(null);
                }, 5000);
            });

            // Process updated forms
            updatedForms.forEach(form => {
                setUpdatedFormIds(prev => new Set(prev).add(form.id));

                // Show update notification
                setNotification({
                    type: 'update',
                    employeeName: form.employee_info?.name || 'Unknown Employee',
                    studentName: form.student_name || 'Unknown Student',
                    formId: form.id,
                    timestamp: form.updated_at,
                    message: 'Student form updated'
                });

                // Auto-expand the employee card
                const employeeKey = `${form.employee_info?.employee_id || 'unknown'}-${form.employee_info?.name || 'Unknown Employee'}`;
                setExpandedEmployees(prev => new Set(prev).add(employeeKey));

                setTimeout(() => {
                    setNotification(null);
                }, 5000);
            });
        }

        // Update previous forms for next comparison
        setPrevForms(forms);
    }, [forms, initialized, prevForms]);

    // ── Auto-clear new/updated badges after 30 seconds ────────────────────
    useEffect(() => {
        const timer = setTimeout(() => {
            setNewFormIds(new Set());
            setUpdatedFormIds(new Set());
        }, 30000);

        return () => clearTimeout(timer);
    }, [forms]);

    // Load data once on mount
    useEffect(() => {
        if (!dataLoaded && !loading && initialized) {
            handleRefresh(false);
            setDataLoaded(true);
        }
    }, [initialized, loading, dataLoaded]);

    useEffect(() => {
        if (initialized && forms.length > 0) setDataLoaded(true);
    }, [forms, initialized]);

    // ── Refresh ──────────────────────────────────────────────────────────────
    const handleRefresh = useCallback(async (resetExpand = true) => {
        setIsRefreshing(true);
        try {
            await refreshData();
            if (resetExpand) {
                setExpandedEmployees(new Set());
                setEmployeePages({});
            }
            setDataLoaded(true);
        } catch (err) {
            console.error('Refresh error:', err);
        } finally {
            if (isMounted.current) setIsRefreshing(false);
        }
    }, [refreshData]);

    // ── Search matcher ──────────────────────────────────────────────────────
    const matchesSearch = useCallback((form, term) => {
        if (!term || term.trim() === '') return true;
        const q = term.toLowerCase().trim();
        return [
            form.student_name, form.center_name, form.enrolment_number,
            form.roll_number_first_year, form.roll_number_second_year,
            form.transaction_id, form.courier_number, form.remark,
            form.employee_info?.name, form.employee_info?.employee_id,
            form.employee_info?.phone, form.employee_info?.email,
            form.employee_info?.department, form.employee_info?.position,
            form.noc_display, form.print_done,
            form.amount_verified ? 'verified' : 'pending',
            form.amount ? form.amount.toString() : '',
            form.printing_date ? formatDate(form.printing_date) : '',
            form.courier_date ? formatDate(form.courier_date) : '',
            formatDate(form.created_at), formatDate(form.updated_at),
        ].some(f => f && f.toString().toLowerCase().includes(q));
    }, []);

    // ── Status matcher ──────────────────────────────────────────────────────
    const matchesStatus = useCallback((form, status) => {
        if (status === 'all') return true;
        if (status === 'pending') return !form.amount_verified;
        if (status === 'verified') return form.amount_verified && form.print_done !== 'yes';
        if (status === 'printed') return form.print_done === 'yes' && !form.courier_number;
        if (status === 'couriered') return !!form.courier_number;
        return true;
    }, []);

    // ── Group forms by employee ─────────────────────────────────────────────
    const groupFormsByEmployee = useCallback(() => {
        const grouped = {};
        forms.forEach(form => {
            const empId = form.employee_info?.employee_id || form.employee || 'unknown';
            const empName = form.employee_info?.name || 'Unknown Employee';
            const key = `${empId}-${empName}`;
            if (!grouped[key]) {
                grouped[key] = { employeeId: empId, employeeName: empName, employeeInfo: form.employee_info || {}, forms: [] };
            }
            grouped[key].forms.push(form);
        });
        Object.values(grouped).forEach(g => {
            g.forms.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        });
        return Object.values(grouped);
    }, [forms]);

    // ── Filtered employees with matching forms ─────────────────────────────
    const filteredEmployees = useMemo(() => {
        const grouped = groupFormsByEmployee();
        return grouped
            .map(group => {
                const matchingForms = group.forms.filter(form =>
                    matchesSearch(form, searchTerm) && matchesStatus(form, filterStatus)
                );
                return { ...group, forms: matchingForms };
            })
            .filter(group => group.forms.length > 0);
    }, [groupFormsByEmployee, searchTerm, filterStatus, matchesSearch, matchesStatus]);

    // ── Pagination helpers ──────────────────────────────────────────────────
    const getPaginatedForms = useCallback((employeeKey, allForms) => {
        const currentPage = employeePages[employeeKey] || 1;
        const pageSize = 3;
        const totalPages = Math.ceil(allForms.length / pageSize);
        const start = (currentPage - 1) * pageSize;
        return {
            forms: allForms.slice(start, start + pageSize),
            currentPage,
            totalPages,
            totalItems: allForms.length,
        };
    }, [employeePages]);

    const handleEmployeePageChange = useCallback((employeeKey, newPage) => {
        setEmployeePages(prev => ({ ...prev, [employeeKey]: newPage }));
    }, []);

    const toggleExpandEmployee = useCallback((employeeKey) => {
        setExpandedEmployees(prev => {
            const next = new Set(prev);
            next.has(employeeKey) ? next.delete(employeeKey) : next.add(employeeKey);
            return next;
        });
    }, []);

    // ── Modal helpers ──────────────────────────────────────────────────────
    const openDetail = useCallback((form) => {
        setSelectedForm(form);
        setModalType('view');
    }, []);

    const openEdit = useCallback((form) => {
        setSelectedForm(form);
        setEditData({
            amount_verified: form.amount_verified || false,
            printing_date: form.printing_date || '',
            print_done: form.print_done || 'pending',
            courier_date: form.courier_date || '',
            courier_number: form.courier_number || '',
            remark: form.remark || '',
        });
        setDateErrors({});
        setModalType('edit');
    }, []);

    const closeModal = useCallback(() => {
        setModalType(null);
        setSelectedForm(null);
        setEditData({});
        setDateErrors({});
    }, []);

    const handleSwitchToEdit = useCallback((form) => {
        openEdit(form);
    }, [openEdit]);

    // ── HR save ─────────────────────────────────────────────────────────────
    const handleHRUpdate = useCallback(async () => {
        try {
            const submitData = {
                amount_verified: editData.amount_verified,
                print_done: editData.print_done,
                remark: editData.remark || '',
            };
            if (editData.printing_date?.trim()) submitData.printing_date = editData.printing_date;
            if (editData.courier_date?.trim()) submitData.courier_date = editData.courier_date;
            if (editData.courier_number?.trim()) submitData.courier_number = editData.courier_number;

            await hrUpdateForm(selectedForm.id, submitData);
            closeModal();
            await handleRefresh(false);
        } catch (err) {
            console.error('HR update error:', err);
            if (err.response?.data?.printing_date || err.response?.data?.courier_date) {
                setDateErrors({
                    printing_date: err.response?.data?.printing_date?.[0] || '',
                    courier_date: err.response?.data?.courier_date?.[0] || '',
                });
            }
        }
    }, [editData, selectedForm, hrUpdateForm, closeModal, handleRefresh]);

    // ── Loading / access / error states ────────────────────────────────────
    if (loading && !initialized) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4" />
                    <p className="text-gray-500">Loading student forms...</p>
                </div>
            </div>
        );
    }

    if (!isAdminOrHR) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FiAlertCircle className="text-red-600 text-3xl" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-800">Access Denied</h2>
                    <p className="text-gray-500 mt-2">You don't have permission to view this page.</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FiAlertCircle className="text-red-600 text-3xl" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-800">Error Loading Forms</h2>
                    <p className="text-gray-500 mt-2">{error}</p>
                    <button onClick={() => handleRefresh(true)}
                        className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    const totalMatchingForms = filteredEmployees.reduce((acc, g) => acc + g.forms.length, 0);

    // ── Render ──────────────────────────────────────────────────────────────
    return (
        <div className="animate-fade-in pb-8">
            {/* Toast Notification */}
            <ToastNotification notification={notification} onClose={() => setNotification(null)} />

            {/* Header */}
            <div className="mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-3">
                            <FiUsers className="text-indigo-600" />
                            Student Forms Dashboard
                            {newFormIds.size > 0 && (
                                <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-green-500 text-white animate-pulse">
                                    {newFormIds.size} New
                                </span>
                            )}
                            {updatedFormIds.size > 0 && newFormIds.size === 0 && (
                                <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500 text-white animate-pulse">
                                    {updatedFormIds.size} Updated
                                </span>
                            )}
                        </h1>
                        <p className="text-gray-500 mt-1">Manage and track student enrollment forms</p>
                    </div>
                    <button
                        onClick={() => handleRefresh(true)}
                        disabled={isRefreshing}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition text-sm flex items-center gap-2 disabled:opacity-50"
                    >
                        <FiRefreshCw size={16} className={isRefreshing || loading ? 'animate-spin' : ''} />
                        {isRefreshing ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                {[
                    { label: 'Total Forms', value: forms.length, icon: <FiFileText />, cls: 'from-indigo-50 to-indigo-100 border-indigo-200', val: 'text-indigo-700', bg: 'bg-indigo-200' },
                    { label: 'Pending', value: forms.filter(f => !f.amount_verified).length, icon: <FiClock />, cls: 'from-red-50 to-red-100 border-red-200', val: 'text-red-700', bg: 'bg-red-200' },
                    { label: 'Verified', value: forms.filter(f => f.amount_verified && f.print_done !== 'yes').length, icon: <FiCheckCircle />, cls: 'from-yellow-50 to-yellow-100 border-yellow-200', val: 'text-yellow-700', bg: 'bg-yellow-200' },
                    { label: 'Completed', value: forms.filter(f => f.courier_number).length, icon: <FiSend />, cls: 'from-green-50 to-green-100 border-green-200', val: 'text-green-700', bg: 'bg-green-200' },
                ].map(s => (
                    <div key={s.label} className={`stat-card bg-gradient-to-br ${s.cls}`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-gray-600 font-medium">{s.label}</p>
                                <p className={`text-2xl font-bold ${s.val}`}>{s.value}</p>
                            </div>
                            <div className={`w-10 h-10 ${s.bg} rounded-xl flex items-center justify-center ${s.val} text-lg`}>
                                {s.icon}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Search + Filter */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by employee, student, enrolment, center, transaction, courier..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-9 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 bg-white"
                        />
                        {searchTerm && (
                            <button onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                <FiX size={16} />
                            </button>
                        )}
                    </div>
                    <div className="w-full sm:w-48 relative">
                        <FiFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 bg-white appearance-none"
                        >
                            <option value="all">All Forms</option>
                            <option value="pending">Pending</option>
                            <option value="verified">Verified</option>
                            <option value="printed">Printed</option>
                            <option value="couriered">Couriered</option>
                        </select>
                    </div>
                </div>
                {(searchTerm || filterStatus !== 'all') && (
                    <div className="mt-2 text-xs text-gray-500">
                        Found {totalMatchingForms} matching form{totalMatchingForms !== 1 ? 's' : ''}
                        {searchTerm && filterStatus !== 'all' && ' (search + filter combined)'}
                    </div>
                )}
            </div>

            {/* Employee Groups */}
            {filteredEmployees.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FiFileText className="text-gray-400 text-2xl" />
                    </div>
                    <p className="text-gray-500">No matching records found.</p>
                    <p className="text-sm text-gray-400 mt-1">Try adjusting your search or filters</p>
                    {(searchTerm || filterStatus !== 'all') && (
                        <button onClick={() => { setSearchTerm(''); setFilterStatus('all'); }}
                            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition text-sm">
                            Clear Filters
                        </button>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredEmployees.map((group) => {
                        const employeeKey = `${group.employeeId}-${group.employeeName}`;
                        const isExpanded = expandedEmployees.has(employeeKey);
                        const paginatedData = getPaginatedForms(employeeKey, group.forms);

                        // Check if this employee has any new forms
                        const hasNewForm = group.forms.some(f => newFormIds.has(f.id));
                        const hasUpdatedForm = group.forms.some(f => updatedFormIds.has(f.id));

                        return (
                            <div
                                key={employeeKey}
                                className={`bg-white rounded-2xl shadow-sm border-2 overflow-hidden transition-all hover:shadow-md ${hasNewForm ? 'border-green-400 animate-glow-green' :
                                    hasUpdatedForm ? 'border-blue-400 animate-glow-blue' :
                                        'border-gray-200'
                                    }`}
                            >
                                {/* Employee Header */}
                                <div
                                    className={`p-4 cursor-pointer hover:bg-gray-50 transition flex items-center justify-between ${hasNewForm ? 'bg-green-50/50' :
                                        hasUpdatedForm ? 'bg-blue-50/50' : ''
                                        }`}
                                    onClick={() => toggleExpandEmployee(employeeKey)}
                                >
                                    <div className="flex items-center gap-4 flex-1 min-w-0">
                                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                                            {group.employeeName?.charAt(0) || 'E'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="font-semibold text-gray-800">{group.employeeName}</p>
                                                <span className="text-xs text-gray-500">ID: {group.employeeId}</span>
                                                {hasNewForm && (
                                                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-500 text-white animate-pulse">
                                                        NEW
                                                    </span>
                                                )}
                                                {hasUpdatedForm && !hasNewForm && (
                                                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500 text-white animate-pulse">
                                                        UPDATED
                                                    </span>
                                                )}
                                                {(searchTerm || filterStatus !== 'all') && (
                                                    <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                                                        {group.forms.length} match{group.forms.length !== 1 ? 'es' : ''}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                                                <span>Showing: {group.forms.length}</span>
                                                <span>•</span>
                                                <span>Pending: {group.forms.filter(f => !f.amount_verified).length}</span>
                                                <span>•</span>
                                                <span>Verified: {group.forms.filter(f => f.amount_verified && f.print_done !== 'yes').length}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <span className={`text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                                        <FiChevronDown size={20} />
                                    </span>
                                </div>

                                {/* Expanded Forms */}
                                {isExpanded && (
                                    <div className="border-t border-gray-100 p-4 bg-gray-50">
                                        {paginatedData.forms.length === 0 ? (
                                            <p className="text-center text-gray-500 text-sm py-4">No forms to display</p>
                                        ) : (
                                            <div className="space-y-3">
                                                {paginatedData.forms.map((form) => {
                                                    const recent = isRecentlyUpdated(form.updated_at);
                                                    const isNew = newFormIds.has(form.id);
                                                    const isUpdated = updatedFormIds.has(form.id);

                                                    return (
                                                        <div
                                                            key={form.id}
                                                            className={`bg-white rounded-xl shadow-sm border-l-4 ${getCardBorderColor(form)} overflow-hidden transition-all hover:shadow-md ${isNew ? 'animate-slide-down border-green-400' :
                                                                isUpdated ? 'animate-pulse-subtle border-blue-400' : ''
                                                                }`}
                                                        >
                                                            <div className="p-3">
                                                                <div className="flex items-center justify-between">
                                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                                                                            {form.student_name?.charAt(0) || 'S'}
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                                <p className="font-semibold text-gray-800 text-sm truncate">{form.student_name}</p>
                                                                                {isNew && (
                                                                                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-500 text-white animate-pulse">
                                                                                        NEW
                                                                                    </span>
                                                                                )}
                                                                                {isUpdated && !isNew && (
                                                                                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500 text-white animate-pulse">
                                                                                        UPDATED
                                                                                    </span>
                                                                                )}
                                                                                {recent && !isNew && !isUpdated && (
                                                                                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-500 text-white">Updated</span>
                                                                                )}
                                                                                {getStatusBadge(form)}
                                                                            </div>
                                                                            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mt-0.5">
                                                                                <span>Enrolment: {form.enrolment_number}</span>
                                                                                <span>•</span>
                                                                                <span>Amount: {formatCurrency(form.amount)}</span>
                                                                                <span>•</span>
                                                                                <span>{formatDistanceToNow(new Date(form.created_at), { addSuffix: true })}</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex items-center gap-2 flex-shrink-0">
                                                                        <button
                                                                            onClick={(e) => { e.stopPropagation(); openDetail(form); }}
                                                                            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                                                                            title="View Details"
                                                                        >
                                                                            <FiEye size={14} />
                                                                        </button>
                                                                        <button
                                                                            onClick={(e) => { e.stopPropagation(); openEdit(form); }}
                                                                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                                                                            title="HR Update"
                                                                        >
                                                                            <FiEdit2 size={14} />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                                                    <div><span className="text-gray-500">Center:</span> <span className="text-gray-700">{form.center_name || 'N/A'}</span></div>
                                                                    <div><span className="text-gray-500">Print:</span> <span className="text-gray-700">{form.print_done || 'pending'}</span></div>
                                                                    <div><span className="text-gray-500">Courier:</span> <span className="text-gray-700">{form.courier_number || 'N/A'}</span></div>
                                                                    <div><span className="text-gray-500">Verified:</span> <span className="text-gray-700">{form.amount_verified ? 'Yes' : 'No'}</span></div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        <EmployeePagination
                                            employeeKey={employeeKey}
                                            currentPage={paginatedData.currentPage}
                                            totalPages={paginatedData.totalPages}
                                            totalItems={paginatedData.totalItems}
                                            onPageChange={handleEmployeePageChange}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Modals */}
            {modalType === 'view' && (
                <DetailModal
                    selectedForm={selectedForm}
                    onClose={closeModal}
                    onEdit={handleSwitchToEdit}
                />
            )}
            {modalType === 'edit' && (
                <EditModal
                    selectedForm={selectedForm}
                    editData={editData}
                    setEditData={setEditData}
                    dateErrors={dateErrors}
                    setDateErrors={setDateErrors}
                    onClose={closeModal}
                    onSave={handleHRUpdate}
                />
            )}

            {/* CSS Animations */}
            <style jsx>{`
                @keyframes slide-in-right {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                .animate-slide-in-right {
                    animation: slide-in-right 0.5s ease-out;
                }

                @keyframes progress-shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }
                .animate-progress-shrink {
                    animation: progress-shrink 5s linear forwards;
                }

                @keyframes glow-green {
                    0%, 100% { border-color: #4ade80; box-shadow: 0 0 20px rgba(74, 222, 128, 0.3); }
                    50% { border-color: #22c55e; box-shadow: 0 0 40px rgba(74, 222, 128, 0.6); }
                }
                .animate-glow-green {
                    animation: glow-green 2s ease-in-out 3;
                }

                @keyframes glow-blue {
                    0%, 100% { border-color: #60a5fa; box-shadow: 0 0 20px rgba(96, 165, 250, 0.3); }
                    50% { border-color: #3b82f6; box-shadow: 0 0 40px rgba(96, 165, 250, 0.6); }
                }
                .animate-glow-blue {
                    animation: glow-blue 2s ease-in-out 3;
                }

                @keyframes slide-down {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-slide-down {
                    animation: slide-down 0.5s ease-out;
                }

                @keyframes pulse-subtle {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.7; }
                }
                .animate-pulse-subtle {
                    animation: pulse-subtle 2s ease-in-out 3;
                }

                @keyframes fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                .animate-fade-in {
                    animation: fade-in 0.5s ease-out;
                }
            `}</style>
        </div>
    );
};

export default HRStudentFormDashboard;